// Progress — the per-child state object that App threads through every
// screen. This is the canonical shape. defaultProgress() returns a
// fresh instance; loadStudent (in learning/studentPersistence.ts) is
// responsible for v2-engine derived fields under engineState.
//
// Persistence boundary: anywhere this object is written to disk
// (childrenStore.saveProgressForChild) or to the cloud (App/useCloudSync)
// MUST receive a Progress-shaped object. Anywhere it's read back, the
// caller should treat unknown fields as legacy artifacts and not depend
// on them — see Progress[K_LEGACY] for the names that exist on disk
// but are no longer part of the canonical shape.

import { MATH_MODULES } from "../curriculum/mathModules.js";
import { READING_MODULES } from "../curriculum/readingModules.js";
import { loadProgressForChild } from "../childrenStore.js";
import { defaultsForAge, applyPriorBoost } from "../engine/ageStartModules.ts";

/** Possible placement-status states. Persisted; new values must be
 * additive so older clients don't crash on read. */
export type PlacementStatus = "not_started" | "in_progress" | "complete";

/**
 * MA-775 — the provenance of a strand's start module (its placement floor
 * once placement is complete). "age": the value was derived from the child's
 * age (onboarding seed, a Settings age re-derive, the level check's
 * age-default proposal). "diagnostic": the onboarding diagnostic RAISED the
 * module from its answers. Only a non-"age" floor is exempt from the age
 * masks in the mission pick (useTodaysMission.ts, isEvidenceFloor).
 */
export type ModuleSource = "age" | "diagnostic";
export type ModuleStrand = "math" | "reading";
export interface ModuleProvenanceEntry {
  /** The module id this entry describes. An entry whose id differs from the
   *  strand's current module is stale and reads as no record (a writer that
   *  records nothing — the cloud levels pull, a fresh-device hydration —
   *  moved the module since). */
  moduleId: string;
  source: ModuleSource;
}
export interface ModuleProvenance {
  math?: ModuleProvenanceEntry | null;
  reading?: ModuleProvenanceEntry | null;
}

interface ModulesLike {
  mathModuleId?: string | null;
  readingModuleId?: string | null;
  moduleProvenance?: ModuleProvenance | null;
}

const moduleKey = (strand: ModuleStrand) =>
  strand === "math" ? ("mathModuleId" as const) : ("readingModuleId" as const);

/** MA-775 — the recorded source of the strand's CURRENT module, or null when
 *  there is no record for it (absent, stale, or malformed). */
export function recordedModuleSource(
  progress: ModulesLike | null | undefined,
  strand: ModuleStrand,
): ModuleSource | null {
  const current = progress?.[moduleKey(strand)];
  const entry = progress?.moduleProvenance?.[strand];
  if (!current || !entry || typeof entry !== "object" || entry.moduleId !== current) return null;
  return entry.source === "age" ? "age" : entry.source === "diagnostic" ? "diagnostic" : null;
}

/** MA-775 — true when the strand has ANY provenance entry, current or stale.
 *  A stale entry means a writer that records nothing (the cloud levels pull,
 *  an engine advance) moved the module AFTER provenance existed on this
 *  device; no entry at all means a pre-MA-775 row or a fresh device. */
export function hasModuleProvenanceEntry(
  progress: ModulesLike | null | undefined,
  strand: ModuleStrand,
): boolean {
  const entry = progress?.moduleProvenance?.[strand];
  return !!entry && typeof entry === "object" && typeof entry.moduleId === "string";
}

/** MA-775 — the age-seed facts the mission pick needs, as plain DATA (the
 *  ADR-069 / MA-131 boundary keeps src/lib/engine from importing
 *  placementEngine; this is the parentPriors pattern — the progress layer
 *  hands the engine an already-computed result). Built on the LEAF module
 *  engine/ageStartModules.ts, never placementEngine, so this hub does not
 *  make the banned modules transitively reachable (RED M2):
 *  - `own`: the seed onboarding writes for `age`
 *    (applyPriorBoost(defaultsForAge(age), priors)); null for an unknown age;
 *  - `anyBand`: every band's seed, with and without the priors boost — the
 *    values an age-derived writer has ever produced (onboarding, the pre-MA-81
 *    Settings change that kept the OLD age's seed, the midpoint-era seed read
 *    half a band high before MA-79 = the next band's seed, the level check). */
export interface AgeSeedModules {
  own: { mathModuleId: string; readingModuleId: string } | null;
  anyBand: { math: string[]; reading: string[] };
}
const SEED_AGES = [4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5];
export function ageSeedModules(
  age: number | null | undefined,
  priors: ParentPriors | null | undefined,
): AgeSeedModules {
  const math = new Set<string>();
  const reading = new Set<string>();
  for (const a of SEED_AGES) {
    for (const d of [defaultsForAge(a), applyPriorBoost(defaultsForAge(a), priors ?? null)]) {
      math.add(d.mathModuleId);
      reading.add(d.readingModuleId);
    }
  }
  const own =
    typeof age === "number" && Number.isFinite(age)
      ? applyPriorBoost(defaultsForAge(age), priors ?? null)
      : null;
  return {
    own: own ? { mathModuleId: own.mathModuleId, readingModuleId: own.readingModuleId } : null,
    anyBand: { math: [...math], reading: [...reading] },
  };
}

/**
 * MA-775 — the provenance to store with a module write. Per strand:
 *  - the module changed (prev → next) → `{ moduleId: next, source }`;
 *  - unchanged, with a record for it → that record is kept;
 *  - unchanged, no record, and equal to `ageStart`'s module → "age";
 *  - otherwise no entry (unknown; the reader's interim rule decides).
 * A strand absent from `next` is treated as unchanged. Pure.
 */
export function nextModuleProvenance(
  prev: ModulesLike | null | undefined,
  next: { mathModuleId?: string | null; readingModuleId?: string | null },
  source: ModuleSource,
  ageStart?: { mathModuleId?: string | null; readingModuleId?: string | null } | null,
): ModuleProvenance {
  const out: ModuleProvenance = {};
  for (const strand of ["math", "reading"] as const) {
    const key = moduleKey(strand);
    const was = prev?.[key] ?? null;
    const now = next[key] ?? was;
    if (!now) continue;
    const recorded = recordedModuleSource(prev, strand);
    if (now !== was) out[strand] = { moduleId: now, source };
    else if (recorded) out[strand] = { moduleId: now, source: recorded };
    else if (ageStart && ageStart[key] === now) out[strand] = { moduleId: now, source: "age" };
  }
  return out;
}

/** A completed-mission record (Mission 1/2/3 of the daily trail). */
export interface MissionRecord {
  missionIndex: number;
  domain: string;
  moduleId: string;
  accuracy: number;
  correct: number;
  total: number;
  completedAt: string;
}

/** A summary of a single completed session, kept for the recent-sessions
 * panel in the dashboard. Capped at 100 entries on the producer side. */
export interface SessionSummary {
  domain: string;
  moduleId: string;
  accuracy: number;
  correct: number;
  total: number;
  date: string;
}

/** Parent-onboarding signals that calibrate the placement target. */
export interface ParentPriors {
  canAddWithin10?: "yes" | "no" | "not_sure";
  canAddWithin20?: "yes" | "no" | "not_sure";
  canReadSimpleWords?: "yes" | "no" | "not_sure";
  canReadSentences?: "yes" | "no" | "not_sure";
  handwritingEasy?: "yes" | "no" | "not_sure";
  stylusAvailable?: "yes" | "no";
  // T1.1a: ability-slug priors captured at /onboarding/kid/abilities, each
  // stored as `{ [slug]: true }` (see lib/engine/parentPriors). An INITIAL
  // POSITION HINT consumed by recommendStartingSkill — NOT a mastery claim,
  // never written to the mastery model. The index signature coexists with
  // the legacy named fields above (read by placementEngine.applyPriorBoost,
  // which ignores unknown slug keys).
  //
  // P-ONBOARD-A: behavior-anchored abilities survey switches from a binary
  // checkbox to a 4-way response scale (Usually on their own / Sometimes
  // with help / Not yet / Not sure). The response is persisted via a FLAT
  // sibling pattern:
  //   - `<slug>: true` when the response was "Usually on their own".
  //     Anything else writes `<slug>: false`; the engine's existing
  //     tickedAbilitySlugs(`=== true`) rule means the consumption is
  //     unchanged (only "Usually" influences recommendStartingSkill today).
  //   - `<slug>_confidence: "usually" | "sometimes" | "not_yet" | "unsure"`
  //     captures the FULL response so the parent dashboard + future
  //     P-ONBOARD-B band-shift calibration can read it. Back-compat:
  //     legacy priors WITHOUT a `_confidence` sibling (T1.1a-era records
  //     stored as `{ slug: true }`) read as confidence "usually" by
  //     convention — the parent ticked it, so they were confident.
  [abilitySlug: string]:
    | boolean
    | "yes" | "no" | "not_sure"
    | "usually" | "sometimes" | "not_yet" | "unsure"
    | undefined;
}

/** P-ONBOARD-A: the 4 responses on the behavior-anchored abilities
 *  survey. Persisted as `<slug>_confidence` on ParentPriors. */
export type AbilityResponse = "usually" | "sometimes" | "not_yet" | "unsure";

/** v2-engine-derived state. Owned by learning/studentPersistence; this
 * shape is intentionally permissive here so the persistence module can
 * evolve without breaking the Progress type. */
export interface EngineState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  masteryRecords?: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reviewQueue?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sessionHistory?: any[];
  currentConfidence?: number;
  frustrationRisk?: number;
  preferredSupportLevel?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  conceptCardLog?: Record<string, any>;
}

export interface Progress {
  // ── Identity + onboarding ──
  studentName: string;
  studentAge: number | null;
  studentGrade: string;
  parentPriors: ParentPriors;
  onboardingComplete: boolean;
  mascot: { id: string } | null;

  // ── Placement + module pointers ──
  placementStatus: PlacementStatus;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  placementResults: Record<string, any> | null;
  // MA-155 (reading-diagnostic-screen-wiring) — the reading phase's own
  // placement result (real content + real staircase; ADR-081/ADR-058).
  // Deliberately separate from placementResults (math-only structurally —
  // applyPlacementBias's one caller hardcodes strand:"math"): no downstream
  // bias consumer reads this yet. Optional + absent on every pre-feature
  // record, same convention as assignedSkillIds/autoReadEnabled above.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readingPlacementResults?: Record<string, any> | null;
  placementCompletedAt?: number;
  placementValidation: unknown;
  mathModuleId: string;
  readingModuleId: string;
  // MA-39 — LWW guard stamp for the pair above (mirrors students.
  // levels_updated_at, migration 0025). Set to the ISO timestamp at the
  // moment mathModuleId/readingModuleId actually change locally (placement
  // finish, placement-skip seed, an engine promotion/walkback, or a parent's
  // explicit level override) — NOT at sync time, so a stale device's later
  // sync can't out-race a fresher device's more recent change. Optional +
  // absent on every pre-feature record, same convention as autoReadEnabled;
  // useCloudSync.syncToCloud falls back to "now" when absent (identical to
  // the pre-guard blind-write behaviour — never a regression).
  levelsUpdatedAt?: string | null;
  // MA-775 — where each strand's CURRENT module came from (see
  // ModuleProvenance below). Device-local, optional + absent on every
  // pre-feature record; a missing or stale entry reads as "unknown" and the
  // mission pick falls back to the interim age-seed test.
  moduleProvenance?: ModuleProvenance | null;

  // day previously fed the deleted Storybook Journey's "Day N" display
  // (useStorybookPath.ts, removed MA-343/ADR-099). No surviving reader —
  // kept on the record shape rather than removed to avoid a schema churn
  // for a single dead field; not part of the deleted streak/reward cluster.
  day: number;
  sessionCount: number;
  sessions: SessionSummary[];
  missions: MissionRecord[];
  missionDate: string | null;
  challengeLevel: number;
  completedStories: string[];

  // ── Select-to-assign queue (parent skill tree) ──
  // Ordered list of micro-skill ids the parent has tapped to "introduce
  // next". The kid meets these in their normal adaptive sessions
  // (useTodaysMission prefers the queue head as the focal skill). FIFO:
  // assignedSkillIds[0] is steered to first. Entries drop off once the kid
  // has been introduced to / mastered the skill (see lib/engine/
  // assignedSkills.ts). Optional + absent on every pre-feature record, so
  // normalizeAssignedQueue treats a missing field as an empty queue.
  assignedSkillIds?: string[];

  // MA-395 — LWW guard stamp for assignedSkillIds cloud sync (mirrors
  // levelsUpdatedAt/students.levels_updated_at, migration 0038). Stamped by
  // SkillTree.tsx's toggleAssign at the moment the parent's OWN device
  // changes the queue — NOT at sync time — so a stale device's later sync
  // can't out-race a fresher device's more recent toggle. Optional + absent
  // on every pre-feature record, same convention as levelsUpdatedAt.
  assignedSkillIdsUpdatedAt?: string | null;

  // MA-395 — this device's last-observed RAW cloud assigned_skill_ids
  // (the column value, before any local merge), refreshed on every cloud
  // pull AND after every successful cloud push. Used ONLY to tell a
  // teacher-RPC addition (present in a fresh cloud fetch but absent here)
  // apart from a parent's own deliberate un-assign (present here,
  // intentionally missing from the array being pushed) — see
  // updateAssignedSkillIdsSafe in supabaseClient.js. Absent/undefined means
  // "this device has never observed a cloud baseline"; the writer then
  // conservatively treats EVERY id currently in the cloud row as unknown
  // and keeps it, rather than risk dropping something it's never seen.
  assignedSkillsCloudMirror?: string[];

  // ── Removable served "Now" (MA-89, ADR-041 §4 / ADR-052) ──
  // SUBTRACTIVE set of engine-served (practicing/next) skill ids a parent
  // has removed from the mission. Distinct from assignedSkillIds (additive
  // parent intent): a suppressed skill is one the ENGINE would otherwise
  // serve that the parent opted OUT of, not one they opted into. See
  // lib/engine/suppressedSkills.ts for the normalize/toggle rules and
  // lib/engine/skillTreeStatus.ts for how a suppressed skill keeps the tree
  // fold chain-alive (never leaves a hole). Optional + absent on every
  // pre-feature record, so normalizeSuppressedSet treats a missing field as
  // an empty set.
  suppressedSkillIds?: string[];

  // MA-395 (suppressed-skill-ids-cloud-sync slice) — LWW guard stamp for
  // suppressedSkillIds cloud sync (mirrors assignedSkillIdsUpdatedAt /
  // students.suppressed_skill_ids_updated_at, migration 0039). Stamped by
  // SkillTree.tsx's toggleSuppress (directly) or toggleAssign (via its
  // disjointness clear) at the moment the parent's OWN device changes the
  // set — NOT at sync time — so a stale device's later sync can't out-race
  // a fresher device's more recent toggle. Optional + absent on every
  // pre-feature record, same convention as assignedSkillIdsUpdatedAt.
  suppressedSkillIdsUpdatedAt?: string | null;

  // Kid's own after-session "how was that?" tap (SessionRecap's
  // DifficultyFeedback). SELF-REPORT, NOT EVIDENCE: never feeds
  // deriveMasteryBand / computeSkillMasteryState / confirmedSecure — it
  // adjusts only the NEXT session's mount-time start tier, by at most one
  // tier, for the skill that was focal when it was given, and it expires.
  // DEVICE-LOCAL: this field lives only in this device's Progress object
  // (localStorage), same as every other field in this file — it is NOT
  // synced to the cloud and does not propagate to a second device. The
  // parent-visible, cross-device record of a tap is the separate
  // `session_feedback` milestone event (milestoneLogger.ts's
  // logSessionFeedback) — that is the cloud record; this slot is only the
  // engine's own next-mount-tier input on THIS device.
  sessionFeedback?: {
    skillId: string;          // the focal skill of the rated session
    level: "easy" | "right" | "tricky";
    at: string;               // ISO-8601 — drives the decay
  } | null;

  // ── Auto-read parent setting (MA-183) ──
  // Tri-state explicit override for the story reader's auto-narrate
  // behavior: true/false = the parent's own choice; undefined/null = no
  // override yet, so src/engine/autoReadPolicy.ts's resolveAutoRead() falls
  // back to the age-band default. Mirrors students.auto_read_enabled (see
  // supabase/migrations/0020_auto_read_pref.sql) — optional + absent on
  // every pre-feature record, same convention as assignedSkillIds above.
  autoReadEnabled?: boolean | null;
  // MA-792 — true while autoReadEnabled is a local choice the cloud has not
  // confirmed; the pull merge keeps the local value and re-pushes it.
  autoReadPendingSync?: boolean;
  // MA-1024 — true while `mascot` is a local pick the cloud has not
  // confirmed; the pull merge keeps the local pal and re-pushes it, and
  // syncToCloud sends mascot_id only while it is set.
  mascotPendingSync?: boolean;

  // ── v2 engine state ──
  engineState?: EngineState;

  // ── Legacy fields (drain naturally) ──
  // skillStates, factPerformance, reviewRecords were retired during the
  // engine consolidation. They may exist on disk for returning users but
  // are no longer part of the canonical type. New code should NOT read
  // or write them. Their presence on disk is harmless.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [legacyField: string]: any;
}

/** The placeholder `defaultProgress()` writes before a child is named. It is
 *  NOT a child's name: surfaces that address the child by name must treat it
 *  as "no name" (MA-907). */
export const DEFAULT_STUDENT_NAME = "Student";

/** MA-907 — the first name the offline banner addresses, or null for the
 *  banner's neutral wording. Null when there is no progress or no name, and
 *  when the name is the default placeholder (compared trimmed and
 *  case-insensitively against `DEFAULT_STUDENT_NAME`, whole name only, so
 *  " Student " and "student" are the placeholder but "Student Lopez" is a
 *  real two-word name). Otherwise the first whitespace-separated word. */
export function offlineBannerFirstName(
  progress: { studentName?: unknown } | null | undefined,
): string | null {
  const full = childDisplayName(progress)?.trim();
  if (!full) return null;
  return full.split(/\s+/)[0] || null;
}

/** MA-965 — the ONE read of `progress.studentName` for every surface that
 *  shows or says the child's name. Returns the stored name unchanged (each
 *  surface keeps its own first-name / trim handling, so a real name renders
 *  exactly as before), or null when there is no progress, no name, a blank
 *  name, a non-string value, or the `DEFAULT_STUDENT_NAME` placeholder (MA-907's rule: trimmed,
 *  case-insensitive, whole name only, so "Student Lopez" is a real name).
 *  Callers fall back to their OWN existing word ("friend", "your child",
 *  "Your child", or no name). Writes, merges and hydration keep using the
 *  raw field. */
export function childDisplayName(
  progress: { studentName?: unknown } | null | undefined,
): string | null {
  const raw = progress?.studentName;
  if (typeof raw !== "string") return null;
  return isPlaceholderStudentName(raw) ? null : raw;
}

/** MA-965 — true when a stored name is missing, blank or the placeholder
 *  (trimmed, case-insensitive against `DEFAULT_STUDENT_NAME`). */
export function isPlaceholderStudentName(name: unknown): boolean {
  if (name == null) return true;
  const full = String(name).trim();
  return !full || full.toLowerCase() === DEFAULT_STUDENT_NAME.toLowerCase();
}

export function defaultProgress(): Progress {
  return {
    onboardingComplete: false,
    placementStatus: "not_started",
    placementResults: null,
    studentName: DEFAULT_STUDENT_NAME,
    studentAge: null,
    studentGrade: "Kindergarten",
    parentPriors: {},
    mathModuleId: MATH_MODULES[0].id,
    readingModuleId: READING_MODULES[0].id,
    day: 1,
    sessionCount: 0,
    sessions: [],
    placementValidation: null,
    missions: [],
    missionDate: null,
    challengeLevel: 0,
    completedStories: [],
    assignedSkillIds: [],
    suppressedSkillIds: [],
    mascot: null,
  };
}

/**
 * Multi-child progress loader. childId=null returns defaults (used
 * before a child is selected on multi-child accounts).
 */
export function loadProgressForActiveChild(childId: string | null): Progress {
  if (!childId) return defaultProgress();
  return loadProgressForChild(childId, defaultProgress) as Progress;
}
